/**
 * AuditLog.jsx — Company-wide role-change audit feed.
 *
 * Surfaces every entry in the backend `role_history` collection (via
 * GET /api/admin/role-history) on one chronological timeline so admins
 * can answer "who changed whose role, when, and why" without
 * cross-referencing the per-user history dialog inside ManageUsers.
 *
 * Access: admin / super_user only (backend enforces; this page also
 * redirects on 403).
 *
 * Features:
 *   - Timeline (newest first).
 *   - Filter by actor, target, old/new role, and a text search across
 *     all four fields.
 *   - CSV export of the current filtered slice for compliance reviews.
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, Search, Download, RotateCcw } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DeletedInvoicesCard } from '@/components/DeletedInvoicesCard';
import { InvoiceNumberingAuditCard } from '@/components/InvoiceNumberingAuditCard';

const API = window.__BACKEND_URL__;

const ROLE_BADGE_VARIANT = {
  admin: 'destructive',
  supervisor: 'secondary',
  user: 'default',
  worker: 'default',
};

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const csvEscape = (v) => {
  if (v == null) return '';
  const s = String(v);
  return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function AuditLog() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [filterFromRole, setFilterFromRole] = useState('any');
  const [filterToRole, setFilterToRole] = useState('any');

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/admin/role-history?limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        toast.error('Admin / super-user access only');
        navigate('/dashboard');
        return;
      }
      toast.error(e?.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((ev) => {
      if (filterFromRole !== 'any' && ev.old_role !== filterFromRole) return false;
      if (filterToRole !== 'any' && ev.new_role !== filterToRole) return false;
      if (!q) return true;
      const hay = `${ev.user_name || ''} ${ev.user_email || ''} ${ev.actor_name || ''} ${ev.actor_email || ''} ${ev.reason || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, filterFromRole, filterToRole]);

  const exportCsv = () => {
    const header = [
      'timestamp',
      'target_name',
      'target_email',
      'old_role',
      'new_role',
      'actor_name',
      'actor_email',
      'reason',
      'source',
    ];
    const rows = filtered.map((ev) => [
      ev.created_at,
      ev.user_name,
      ev.user_email,
      ev.old_role,
      ev.new_role,
      ev.actor_name,
      ev.actor_email,
      ev.reason,
      ev.source,
    ].map(csvEscape).join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hines-time_role-audit_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const uniqueRoles = useMemo(() => {
    const s = new Set();
    items.forEach((ev) => {
      if (ev.old_role) s.add(ev.old_role);
      if (ev.new_role) s.add(ev.new_role);
    });
    return Array.from(s).sort();
  }, [items]);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-amber-500" />
            Role-Change Audit Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every role change company-wide on one timeline. Use for compliance reviews
            and spotting suspicious activity. The last 500 events are shown — newest first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchHistory}
            disabled={loading}
            data-testid="audit-log-refresh"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={!filtered.length}
            data-testid="audit-log-export-csv"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, or reason…"
                className="pl-9"
                data-testid="audit-log-search"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={filterFromRole} onValueChange={setFilterFromRole}>
                <SelectTrigger data-testid="audit-log-filter-from">
                  <SelectValue placeholder="From role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">From: any</SelectItem>
                  {uniqueRoles.map((r) => (
                    <SelectItem key={`from-${r}`} value={r}>From: {r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterToRole} onValueChange={setFilterToRole}>
                <SelectTrigger data-testid="audit-log-filter-to">
                  <SelectValue placeholder="To role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">To: any</SelectItem>
                  {uniqueRoles.map((r) => (
                    <SelectItem key={`to-${r}`} value={r}>To: {r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {loading
              ? 'Loading…'
              : `Showing ${filtered.length} of ${items.length} event${items.length === 1 ? '' : 's'}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!loading && filtered.length === 0 && (
            <div className="text-sm text-slate-500 py-10 text-center" data-testid="audit-log-empty">
              No role changes match the current filters.
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="space-y-3" data-testid="audit-log-list">
              {filtered.map((ev) => (
                <div
                  key={ev.id || `${ev.user_id}-${ev.created_at}`}
                  className="border rounded-lg p-3 hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors"
                  data-testid={`audit-log-entry-${ev.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="space-y-1">
                      <div className="text-sm font-medium">
                        <span>{ev.user_name || ev.user_email || ev.user_id}</span>
                        <span className="text-slate-400 mx-2">·</span>
                        <Badge variant={ROLE_BADGE_VARIANT[ev.old_role] || 'outline'}>
                          {ev.old_role || 'none'}
                        </Badge>
                        <span className="text-slate-400 mx-1">→</span>
                        <Badge variant={ROLE_BADGE_VARIANT[ev.new_role] || 'outline'}>
                          {ev.new_role || 'none'}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-500">
                        Changed by{' '}
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                          {ev.actor_name || ev.actor_email || 'system'}
                        </span>
                        {ev.source ? ` · via ${ev.source}` : ''}
                      </div>
                      {ev.reason && (
                        <div className="text-xs italic text-slate-600 dark:text-slate-400">
                          “{ev.reason}”
                        </div>
                      )}
                    </div>
                    <div
                      className="text-xs text-slate-500 whitespace-nowrap"
                      title={ev.created_at}
                      data-testid={`audit-log-entry-${ev.id}-time`}
                    >
                      {formatTimestamp(ev.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <InvoiceNumberingAuditCard />
      <DeletedInvoicesCard />
    </div>
  );
}
